import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Our Mission',
      description: 'To transform spaces through innovative design, sustainable practices, and exceptional craftsmanship.',
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Client-Centric',
      description: 'We prioritize understanding our clients\' needs and delivering personalized solutions that exceed expectations.',
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Quality Excellence',
      description: 'Committed to delivering superior quality in every project through attention to detail and skilled execution.',
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Continuous Innovation',
      description: 'Embracing new technologies and design trends to provide cutting-edge solutions for modern living.',
    },
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '15+', label: 'Years Experience' },
    { number: '50+', label: 'Team Members' },
    { number: '98%', label: 'Client Satisfaction' },
  ];

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#ffffff] via-[#e8f5e9] to-[#c8e6c9] py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            About FCV Constructions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-700 max-w-2xl mx-auto"
          >
            Crafting exceptional spaces with passion, expertise, and dedication
          </motion.p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Transforming Visions into Reality
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  FCV Constructions is a premier architecture and interior design firm based in Raipur, Chhattisgarh. With over 15 years of experience, we have established ourselves as leaders in creating innovative and sustainable living spaces.
                </p>
                <p>
                  Our team of skilled architects, designers, and engineers work collaboratively to bring your dream spaces to life. We combine creativity with functionality, ensuring that every project reflects our commitment to excellence.
                </p>
                <p>
                  From residential homes to commercial complexes, we offer comprehensive services that cover every aspect of construction and design. Our portfolio showcases diverse projects that demonstrate our versatility and attention to detail.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
                alt="About FCV Constructions"
                className="w-full h-[400px] object-cover rounded-xl shadow-2xl"
              />
            </motion.div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gradient-to-br from-[#e8f5e9] to-[#c8e6c9] rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-[#008000] mb-2">{stat.number}</div>
                <div className="text-sm font-medium text-gray-700">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Values */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-[#e8f5e9] to-[#c8e6c9] rounded-full flex items-center justify-center mx-auto mb-4 text-[#008000]">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;